import { Observable, of } from 'rxjs';
import { concatMap, filter, map } from 'rxjs/operators';
import { ApModalReason } from './ApModalReason';

export type ApModalResult<T> = ApModalResultSuccess<T> | ApModalResultFailure;

export interface ApModalResultSuccess<TResult> {
  readonly isSuccess: true;
  readonly reason: ApModalReason.Closed;
  readonly resultValue: TResult;
}

export interface ApModalResultFailure {
  readonly isSuccess: false;
  readonly reason: ApModalReason.UserCancel | ApModalReason.Unknown;

  // this could be an Error, error message, general reason for cancellation, etc
  readonly failureData?: unknown;
}

// This a guard to help for narrowing to ApModalResultSuccess or ApModalResultFailure
export function isModalSuccess<T>(modalResult: ApModalResult<T>): modalResult is ApModalResultSuccess<T> {
  return modalResult.isSuccess;
}

export function mapToModalSuccessValue<T>() {
  return (source: Observable<ApModalResult<T>>): Observable<T> => {
    return source.pipe(
      filter(isModalSuccess),
      map((r) => r.resultValue),
    );
  };
}

// todo: add tests for these functions

// this is an operator that just uses map with _convertModalResult
// Use it when you want to map a successful result to a different type,
// and just let a failure result get returned as normal.
export function convertModalResult<TSource, TTarget>(mapFn: (value: TSource) => TTarget) {
  return map<ApModalResult<TSource>, ApModalResult<TTarget>>((modalResult) => _convertModalResult(modalResult, mapFn));
}

export function _convertModalResult<TSource, TTarget>(
  source: ApModalResult<TSource>,
  mapFn: (value: TSource) => TTarget,
): ApModalResult<TTarget> {
  if (!source.isSuccess) {
    return source;
  }

  return {
    isSuccess: source.isSuccess,
    reason: source.reason,
    resultValue: mapFn(source.resultValue),
  } satisfies ApModalResultSuccess<TTarget>;
}

// this is an operator that just uses map with _convertModalResultP
// Use it when you want to async map a successful result to a different type,
// and just let a failure result get returned as normal.
// It uses concatMap to preserve the order of the emitted values, but for our modals, it's only ever 1.
export function convertModalResultP<TSource, TTarget>(mapFn: (value: TSource) => Promise<TTarget>) {
  return concatMap<ApModalResult<TSource>, Promise<ApModalResult<TTarget>>>(
    async (modalResult) => await _convertModalResultP(modalResult, mapFn),
  );
}

export async function _convertModalResultP<TSource, TTarget>(
  source: ApModalResult<TSource>,
  mapFn: (value: TSource) => Promise<TTarget>,
): Promise<ApModalResult<TTarget>> {
  if (!source.isSuccess) {
    return source;
  }

  return {
    isSuccess: source.isSuccess,
    reason: source.reason,
    resultValue: await mapFn(source.resultValue),
  } satisfies ApModalResultSuccess<TTarget>;
}

// this is an operator that just uses map with _convertModalFailure
// Use it when you want to map a failure result (NOT a thrown error) to a different type,
// and just let a successful result get returned as normal.
export function convertModalFailure<TSource, TFailureResult>(mapFn: (value: ApModalResultFailure) => TFailureResult) {
  return map<ApModalResult<TSource>, TSource | TFailureResult>((modalResult) =>
    _convertModalFailure(modalResult, mapFn),
  );
}

// convert the modal failure, or just return the successful resultValue
export function _convertModalFailure<TSource, TFailureResult>(
  source: ApModalResult<TSource>,
  mapFn: (value: ApModalResultFailure) => TFailureResult,
): TSource | TFailureResult {
  if (!source.isSuccess) {
    return mapFn(source);
  }

  return source.resultValue;
}

export function convertToModalClosed<TSource, TTarget>(mapFn: (value: TSource) => TTarget) {
  return map<TSource, ApModalResult<TTarget>>((value) => apModalClosed(mapFn(value)));
}

export function apModalClosed<T>(result: T): ApModalResult<T> {
  return {
    isSuccess: true,
    reason: ApModalReason.Closed,
    resultValue: result,
  };
}

// return an Observable wrapper of apModalClosed
export function apModalClosed$<T>(result: T): Observable<ApModalResult<T>> {
  return of(apModalClosed(result));
}

export function apModalCancelled<T>(
  reason: ApModalReason.UserCancel | ApModalReason.Unknown,
  failureData?: unknown,
): ApModalResult<T> {
  return {
    isSuccess: false,
    reason,
    failureData,
  };
}
